'use client';

import React, { useState, useEffect } from 'react';
import { AtSign, Save, Check } from 'lucide-react';

interface BankTransferFormProps {
  initialAlias?: string;
  initialHolder?: string;
  initialCbu?: string;
  initialRoundBalance?: boolean;
  onSaveBankDetails: (details: {
    bankAlias: string;
    bankHolder: string;
    bankCbu: string;
    roundBalance: boolean;
  }) => Promise<void>;
}

export function BankTransferForm({
  initialAlias = 'juan.barber.mp',
  initialHolder = 'Juan Rojas',
  initialCbu = '0000003100012345678901',
  initialRoundBalance = false,
  onSaveBankDetails
}: BankTransferFormProps) {
  const [alias, setAlias] = useState(initialAlias);
  const [holder, setHolder] = useState(initialHolder);
  const [cbu, setCbu] = useState(initialCbu);
  const [extraNote, setExtraNote] = useState('');
  const [roundBalance, setRoundBalance] = useState(initialRoundBalance);
  const [saving, setSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  useEffect(() => {
    setAlias(initialAlias);
    setHolder(initialHolder);
    setCbu(initialCbu);
    setRoundBalance(initialRoundBalance);
  }, [initialAlias, initialHolder, initialCbu, initialRoundBalance]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSaveBankDetails({
        bankAlias: alias.trim(),
        bankHolder: holder.trim(),
        bankCbu: cbu.trim(),
        roundBalance
      });
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Transfer Form Box */}
      <div className="space-y-3">
        <div>
          <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            TRANSFERENCIA (RECEPCIÓN)
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Datos que se muestran al cliente cuando elige transferir en persona. Para cobros remotos usá MP Checkout.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-2xs space-y-4">
          {/* Alias */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Alias CBU / CVU
            </label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">
                @
              </span>
              <input
                type="text"
                placeholder="mi.alias.mp"
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                className="w-full pl-8 pr-3.5 py-2.5 bg-slate-50/80 border border-slate-200 rounded-xl text-xs font-mono text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 font-bold"
              />
            </div>
          </div>

          {/* Titular */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Titular de la cuenta
            </label>
            <input
              type="text"
              placeholder="Nombre y Apellido"
              value={holder}
              onChange={(e) => setHolder(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-50/80 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600"
            />
          </div>

          {/* CBU */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              CBU / CVU (22 dígitos · opcional)
            </label>
            <input
              type="text"
              maxLength={22}
              placeholder="0000000000000000000000"
              value={cbu}
              onChange={(e) => setCbu(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-50/80 border border-slate-200 rounded-xl text-xs font-mono text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 tracking-wider"
            />
          </div>

          {/* Nota extra */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-bold text-slate-700">
                Nota extra (opcional)
              </label>
              <span className="text-[10px] text-slate-400">
                {extraNote.length}/240
              </span>
            </div>
            <textarea
              maxLength={240}
              rows={2}
              placeholder="Ej: Guardá el comprobante por si lo pide la recepción"
              value={extraNote}
              onChange={(e) => setExtraNote(e.target.value)}
              className="w-full px-3.5 py-2 bg-slate-50/80 border border-slate-200 rounded-xl text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600"
            />
          </div>

          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-all shadow-sm flex items-center gap-1.5"
            >
              {savedSuccess ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>¡Guardado!</span>
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5" />
                  <span>{saving ? 'Guardando...' : 'Guardar datos bancarios'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Redondeo del Saldo Box */}
      <div className="p-5 bg-white border border-slate-200/90 rounded-2xl shadow-2xs space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            REDONDEO DEL SALDO
          </h4>
          <button
            type="button"
            onClick={() => setRoundBalance(!roundBalance)}
            className={`w-10 h-5 flex items-center rounded-full p-0.5 cursor-pointer transition-colors ${
              roundBalance ? 'bg-emerald-600' : 'bg-slate-300'
            }`}
          >
            <div
              className={`bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${
                roundBalance ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          Si al cobrar queda un resto chico, aparece un botón para perdonarlo y cerrar el turno en $0. Lo perdonado se ve aparte en Finanzas.
        </p>
      </div>
    </div>
  );
}
